/**
 * Authentication utilities for Get Talky Agent
 * 
 * Since JWT token authentication is now handled by the proxy server,
 * this module focuses on client-side auth state management and error handling.
 */

/**
 * Get the auth token endpoint URL based on environment
 */
const getAuthTokenUrl = () => {
  // In production, use relative URL (same origin)
  if (process.env.NODE_ENV === 'production') {
    return '/auth/token';
  }
  
  // For local development, use localhost
  return 'http://localhost:8080/auth/token';
};

/**
 * Check if the proxy server is authenticated and can access the API
 * @returns {Promise<boolean>} Whether the proxy server can authenticate
 */
export const checkAuthStatus = async () => {
  try {
    // Test proxy server authentication by checking the auth/token endpoint
    const response = await fetch(getAuthTokenUrl());
    
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Authentication check passed:', data.message);
      return true;
    } else {
      const errorData = await response.json().catch(() => ({}));
      console.warn('❌ Authentication check failed:', errorData.message || 'Unknown error');
      return false;
    }
  } catch (error) {
    console.warn('❌ Auth check failed:', error.message);
    return false;
  }
};

/**
 * Handle authentication errors
 * @param {Error} error - The authentication error
 */
export const handleAuthError = (error) => {
  console.error('Authentication error:', error);
  
  // Check if it's a 401 or 403 error
  if (error.message.includes('401') || error.message.includes('403')) {
    console.warn('Authentication failed - user may need to re-authenticate with gcloud');
    return {
      type: 'auth_required',
      message: 'Authentication required. Please run "gcloud auth login" and restart the proxy server.',
    };
  }
  
  // Check if it's a network error
  if (error.message.includes('fetch')) {
    return {
      type: 'network_error', 
      message: 'Network error. Please check your connection and ensure the proxy server is running.',
    };
  }
  
  return {
    type: 'unknown_error',
    message: error.message || 'An unknown error occurred.',
  };
};

/**
 * Get user information (basic implementation)
 * @returns {Promise<Object|null>} User information or null if not available
 */
export const getCurrentUser = async () => {
  try {
    const isAuthenticated = await checkAuthStatus();
    
    if (isAuthenticated) {
      return {
        id: 'current_user',
        role: 'agent',
        permissions: ['read', 'write'],
        authenticated: true,
        authMethod: 'gcloud_proxy',
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Initialize authentication (called on app startup)
 * @returns {Promise<Object>} Authentication initialization result
 */
export const initializeAuth = async () => {
  try {
    console.log('🔐 Initializing authentication...');
    const isAuthenticated = await checkAuthStatus();
    
    if (isAuthenticated) {
      const user = await getCurrentUser();
      console.log('✅ Authentication successful');
      return {
        success: true,
        user: user,
        message: 'Authenticated via proxy server'
      };
    } else {
      console.log('❌ Authentication failed');
      return {
        success: false,
        error: 'Proxy server authentication failed - please run "gcloud auth login"',
      };
    }
  } catch (error) {
    console.error('❌ Authentication initialization failed:', error);
    return {
      success: false,
      error: handleAuthError(error),
    };
  }
};

/**
 * Logout utility
 */
export const logout = () => {
  console.log('Logout requested - refreshing page');
  window.location.reload();
};

export default {
  checkAuthStatus,
  handleAuthError,
  getCurrentUser,
  initializeAuth,
  logout,
}; 